import React from 'react';
import { Box, Card, CardContent } from '@mui/material';
import type { ProcessedTicketData } from '../../../types/processedTicket';
import type { ScannerConfig } from '../../../types/features/games';
import type { CaseSimulatorBarcodeMode } from '../../../types/features/caseSimulator';
import { useTicketCardLogic } from '../../../hooks/logic/useTicketCardLogic';
import { translateBarcodeFormat, type BarcodeFormat } from '../../../utils/barcodeFormatTranslator';
import { normalizeGameImageUrl } from '../../../utils/normalizers/gameImageUrl';
import TicketCardActions from './TicketCardActions';
import TicketCardBarcode from './TicketCardBarcode';
import TicketCardErrors from './TicketCardErrors';
import TicketCardHeader from './TicketCardHeader';
import './TicketCard.css';

interface TicketCardProps {
  barcodeMode?: CaseSimulatorBarcodeMode;
  pack: ProcessedTicketData;
  onPackReturn?: (packId: string) => void;
  storeId: string;
  scannerConfig?: ScannerConfig | null;
}

const TicketCard: React.FC<TicketCardProps> = ({
  barcodeMode = 'ticket',
  pack,
  onPackReturn,
  storeId,
  scannerConfig,
}) => {
  const isPluBarcodeMode = barcodeMode === 'plu';
  const {
    ticketsSold,
    ticketInfo,
    formattedGameStartDate,
    formattedGameEndDate,
    formattedActiveDate,
    barcodeValue,
    currentTicketNumber,
    pluBarcode,
    actionOptions,
    selectedAction,
    setSelectedAction,
    isActionDisabled,
    actionLoading,
    errors,
    handleActionSubmit,
  } = useTicketCardLogic({ actionsDisabled: isPluBarcodeMode, pack, onPackReturn, storeId });
  const scannerFormats = scannerConfig?.barcode_scanner_formats || pack.barcode_scanner_formats;
  const ticketBarcodeFormat = translateBarcodeFormat(scannerFormats?.find((format) => format?.trim().length > 0));
  const ticketBarcodeLength = scannerConfig?.barcode_length ?? pack.barcode_length ?? (barcodeValue ? barcodeValue.length : null);
  const ticketBarcodeLayout = scannerConfig?.barcode_format || pack.barcode_format;
  const selectedBarcodeFormat: BarcodeFormat | undefined = isPluBarcodeMode ? 'UPC' : ticketBarcodeFormat;
  const selectedBarcodeLength = isPluBarcodeMode ? pluBarcode.length : ticketBarcodeLength;
  const selectedBarcodeMetaText = isPluBarcodeMode
    ? `Symbology: UPC-A | Length: ${selectedBarcodeLength ?? '-'}`
    : `Symbology: ${ticketBarcodeFormat || '-'} | Length: ${selectedBarcodeLength ?? '-'} | Format: ${ticketBarcodeLayout || '-'}`;

  return (
    <Card elevation={8} className="TicketCard">
      <CardContent className="content">
        <TicketCardHeader
          imageUrl={normalizeGameImageUrl(pack.image_url)}
          gameName={pack.game_name}
          startDateLabel={formattedGameStartDate}
          endDateLabel={formattedGameEndDate}
          pricePoint={pack.price_point}
          packRetailValue={pack.pack_retail_value}
          plu={pack.plu}
          ticketsSold={ticketsSold}
          ticketsPerPack={pack.tickets_per_pack}
          activeDateLabel={formattedActiveDate}
        />

        <Box className="bottom">
          <TicketCardBarcode
            barcodeValue={isPluBarcodeMode ? pluBarcode.barcodeValue : barcodeValue}
            barcodeFormat={selectedBarcodeFormat}
            displayValue={isPluBarcodeMode ? pluBarcode.displayValue : currentTicketNumber}
            isSoldOut={!isPluBarcodeMode && ticketInfo.isSoldOut}
            metaText={selectedBarcodeMetaText}
            unavailableMessage={isPluBarcodeMode ? pluBarcode.error || 'PLU barcode not available' : 'Barcode not available'}
          />

          <TicketCardActions
            actionOptions={actionOptions}
            selectedAction={selectedAction}
            onActionChange={setSelectedAction}
            onSubmit={handleActionSubmit}
            isDisabled={isActionDisabled}
            isSelectionDisabled={isPluBarcodeMode}
            isLoading={actionLoading}
            selectLabelId={`ticket-action-select-label-${pack.pack_id}`}
          />
        </Box>

        <TicketCardErrors
          errors={[errors.saleError, errors.returnError, errors.returnPackError, errors.freeTicketError]}
        />
      </CardContent>
    </Card>
  );
};

export default TicketCard;
